/**
 * modelコマンドハンドラ
 *
 * JSON形式のモデル設定を受け取り、GLMタブを設定済みで開く
 *
 * 注意: 現在はGLMモデルの直接作成ではなく、GLMタブを開いて人間がレビュー・実行する形式
 * 直接実行が必要な場合は、CommandContextにaddModel機能を追加する必要がある
 *
 * 使用例:
 * x:model {"type":"glm","datasetId":"dataset_1","yColumn":"price","xColumns":["area","rooms"],"family":"gaussian","link":"identity"}
 */

import type { CommandHandler, CommandResult, ParsedCommand, CommandContext } from '../types';
import type { TabState } from '../../../types';
import { createTabId, createDatasetId, createColumnId } from '../../../types';
import type { GLMFamily, GLMLink } from '../../../types/model.types';
import logger from '../../utils/logger';

/**
 * x:modelコマンドで受け付けるJSON形式
 */
interface ModelInput {
  type: string;
  datasetId: string;
  yColumn: string;
  xColumns: string[];
  family?: GLMFamily;
  link?: GLMLink;
  interceptIncluded?: boolean;
}

/**
 * GLMタブの初期設定を構築
 */
function buildGLMTabViewState(input: ModelInput): Record<string, unknown> {
  return {
    selectedDatasetId: createDatasetId(input.datasetId),
    selectedYColumn: createColumnId(input.yColumn),
    selectedXColumns: input.xColumns.map(c => createColumnId(c)),
    family: input.family ?? 'gaussian',
    link: input.link ?? 'identity',
    interceptIncluded: input.interceptIncluded ?? true
  };
}

export const modelHandler: CommandHandler = {
  name: 'model',
  description: 'Open GLM tab with pre-configured model settings for review',
  category: 'analysis',
  parameters: [
    {
      name: 'json',
      type: 'string',
      required: false, // JSON-basedコマンドはpositionalArgsを使用するため、ここではfalse
      description: 'JSON object specifying the model configuration'
    }
  ],
  aliases: [],
  examples: [
    'x:model {"type":"glm","datasetId":"dataset_1","yColumn":"price","xColumns":["area","rooms"]}',
    'x:model {"type":"glm","datasetId":"dataset_1","yColumn":"count","xColumns":["time"],"family":"poisson","link":"log"}'
  ],

  execute: async (args: ParsedCommand, context: CommandContext): Promise<CommandResult> => {
    // JSONを取得（位置引数全体を結合）
    const jsonStr = args.positionalArgs.join(' ').trim();

    if (!jsonStr) {
      return {
        success: false,
        message: 'JSON configuration required',
        error: {
          code: 'NO_JSON',
          message: 'No JSON configuration provided',
          suggestion: 'Provide a JSON object: x:model {"type":"glm","datasetId":"...","yColumn":"...","xColumns":[...]}'
        }
      };
    }

    // JSONをパース
    let modelInput: ModelInput;
    try {
      modelInput = JSON.parse(jsonStr) as ModelInput;
    } catch (e) {
      logger.error('[modelHandler] Failed to parse JSON', { jsonStr, error: e });
      return {
        success: false,
        message: 'Invalid JSON format',
        error: {
          code: 'INVALID_JSON',
          message: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
          suggestion: 'Check JSON syntax'
        }
      };
    }

    // 必須フィールドの検証
    if (!modelInput.type) {
      return {
        success: false,
        message: 'Model type required',
        error: {
          code: 'NO_TYPE',
          message: 'Model type is required',
          suggestion: 'Include "type" field (currently only "glm" is supported)'
        }
      };
    }

    if (modelInput.type !== 'glm') {
      return {
        success: false,
        message: `Unsupported model type: ${modelInput.type}`,
        error: {
          code: 'UNSUPPORTED_TYPE',
          message: `Model type "${modelInput.type}" is not supported`,
          suggestion: 'Currently only "glm" type is supported'
        }
      };
    }

    if (!modelInput.datasetId) {
      return {
        success: false,
        message: 'Dataset ID required',
        error: {
          code: 'NO_DATASET',
          message: 'datasetId is required',
          suggestion: 'Include "datasetId" field'
        }
      };
    }

    if (!modelInput.yColumn) {
      return {
        success: false,
        message: 'Response variable required',
        error: {
          code: 'NO_Y',
          message: 'yColumn (response variable) is required',
          suggestion: 'Include "yColumn" field'
        }
      };
    }

    if (!modelInput.xColumns || modelInput.xColumns.length === 0) {
      return {
        success: false,
        message: 'Predictor variables required',
        error: {
          code: 'NO_X',
          message: 'xColumns (predictor variables) are required',
          suggestion: 'Include "xColumns" array with at least one column'
        }
      };
    }

    // ターゲットデータセットの存在確認
    const targetDataset = context.datasets[modelInput.datasetId];
    if (!targetDataset) {
      return {
        success: false,
        message: `Dataset not found: ${modelInput.datasetId}`,
        error: {
          code: 'DATASET_NOT_FOUND',
          message: `Target dataset "${modelInput.datasetId}" not found`,
          suggestion: 'Check available datasets'
        }
      };
    }

    logger.info('[modelHandler] Opening GLM tab with configuration', {
      datasetId: modelInput.datasetId,
      yColumn: modelInput.yColumn,
      xColumns: modelInput.xColumns,
      family: modelInput.family ?? 'gaussian',
      link: modelInput.link ?? 'identity'
    });

    // GLMタブを作成
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const tab: TabState = {
      id: createTabId(`glm-${timestamp}_${random}`),
      type: 'glm',
      title: 'GLM',
      isClosable: true,
      keepMounted: true,
      tabViewState: buildGLMTabViewState(modelInput)
    };

    // タブを追加
    context.addTab(tab);

    return {
      success: true,
      message: `GLM tab opened with ${modelInput.xColumns.length} predictors`,
      tabId: tab.id,
      data: {
        datasetId: modelInput.datasetId,
        yColumn: modelInput.yColumn,
        xColumns: modelInput.xColumns,
        family: modelInput.family ?? 'gaussian',
        link: modelInput.link ?? 'identity'
      }
    };
  }
};
